/**
 * React Query hooks for the Spark Explore admin dashboard.
 *
 * Polling only happens while a collection execution is active, and only
 * against our own status endpoints — never against Google or X. When an
 * active execution finishes, trend queries are invalidated so the table
 * reflects what the run wrote.
 */

import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { exploreAdminApi } from "@/services/explore";
import type {
  ExploreExecutionScope,
  ExploreRecordType,
  ExploreSource,
  ExploreTrendListParams,
} from "@/services/explore";

export const EXPLORE_POLL_INTERVAL_MS = 5_000;
const STALE_TIME = 30_000;

export const exploreKeys = {
  all: ["admin", "explore"] as const,
  overview: () => [...exploreKeys.all, "overview"] as const,
  trends: () => [...exploreKeys.all, "trends"] as const,
  trendList: (params?: ExploreTrendListParams) =>
    [...exploreKeys.trends(), "list", params ?? {}] as const,
  trendDetail: (id: string) => [...exploreKeys.trends(), "detail", id] as const,
  trendRecords: (
    id: string,
    params: { type: ExploreRecordType; source?: ExploreSource; cursor?: string }
  ) => [...exploreKeys.trends(), "records", id, params] as const,
  executions: () => [...exploreKeys.all, "executions"] as const,
  executionList: (cursor?: string) =>
    [...exploreKeys.executions(), "list", cursor ?? null] as const,
  execution: (id: string) =>
    [...exploreKeys.executions(), "detail", id] as const,
};

/** Catalog, pipeline health, active runs and permissions. Polls while a run is active. */
export function useExploreOverview() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: exploreKeys.overview(),
    queryFn: () => exploreAdminApi.getOverview(),
    staleTime: STALE_TIME,
    refetchInterval: (q) =>
      (q.state.data?.activeExecutions.length ?? 0) > 0
        ? EXPLORE_POLL_INTERVAL_MS
        : false,
  });

  // Refresh trends + history once the last active run finishes.
  const activeCount = query.data?.activeExecutions.length ?? 0;
  const previousActive = useRef(activeCount);
  useEffect(() => {
    if (previousActive.current > 0 && activeCount === 0) {
      void queryClient.invalidateQueries({ queryKey: exploreKeys.trends() });
      void queryClient.invalidateQueries({
        queryKey: exploreKeys.executions(),
      });
    }
    previousActive.current = activeCount;
  }, [activeCount, queryClient]);

  return query;
}

export function useExploreTrends(params: ExploreTrendListParams) {
  return useQuery({
    queryKey: exploreKeys.trendList(params),
    queryFn: () => exploreAdminApi.listTrends(params),
    staleTime: STALE_TIME,
  });
}

export function useExploreTrendDetail(id: string | undefined) {
  return useQuery({
    queryKey: exploreKeys.trendDetail(id ?? ""),
    queryFn: () => exploreAdminApi.getTrend(id as string),
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}

/** One additional page of a trend's records (used for "Load more" in the detail view). */
export function useExploreTrendRecords(
  id: string | undefined,
  params: { type: ExploreRecordType; cursor?: string }
) {
  return useQuery({
    queryKey: exploreKeys.trendRecords(id ?? "", params),
    queryFn: () =>
      exploreAdminApi.getTrendRecords(id as string, {
        ...params,
        limit: 25,
      }),
    enabled: !!id && !!params.cursor,
    staleTime: STALE_TIME,
  });
}

export function useExploreExecutions(cursor?: string) {
  return useQuery({
    queryKey: exploreKeys.executionList(cursor),
    queryFn: () => exploreAdminApi.listExecutions({ limit: 10, cursor }),
    staleTime: STALE_TIME,
    refetchInterval: (q) =>
      q.state.data?.items.some((e) => e.active)
        ? EXPLORE_POLL_INTERVAL_MS
        : false,
  });
}

/** One execution's status; polls while it is active. */
export function useExploreExecution(id: string | undefined) {
  return useQuery({
    queryKey: exploreKeys.execution(id ?? ""),
    queryFn: () => exploreAdminApi.getExecution(id as string),
    enabled: !!id,
    refetchInterval: (q) =>
      q.state.data?.active ? EXPLORE_POLL_INTERVAL_MS : false,
  });
}

/**
 * Switch one provider's scheduled collection on or off.
 *
 * The overview is refetched rather than patched locally, so the row shows
 * exactly what the backend stored — including who changed it and when.
 */
export function useSetExploreSourceEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      source,
      enabled,
    }: {
      source: ExploreSource;
      enabled: boolean;
    }) => exploreAdminApi.setSourceEnabled(source, enabled),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: exploreKeys.overview() });
    },
  });
}

/**
 * Trigger a manual collection. The overview and history are refetched on
 * both success and failure — after a 409 that surfaces the run that is
 * already in progress.
 */
export function useTriggerExploreCollection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scope: ExploreExecutionScope) =>
      exploreAdminApi.triggerExecution(scope),
    onSuccess: (execution) => {
      queryClient.setQueryData(exploreKeys.execution(execution.id), execution);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: exploreKeys.overview() });
      void queryClient.invalidateQueries({
        queryKey: exploreKeys.executions(),
      });
    },
  });
}

/**
 * Ask a run in progress to stop.
 *
 * The execution is refetched rather than patched optimistically: the backend
 * only records the request, and what an admin needs to see next is the run
 * actually winding down — `cancelRequested` first, then `cancelled` a few
 * seconds later when the collectors have honoured it.
 */
export function useCancelExploreCollection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => exploreAdminApi.cancelExecution(id),
    onSuccess: (execution) => {
      queryClient.setQueryData(exploreKeys.execution(execution.id), execution);
    },
    onSettled: (_data, _error, id) => {
      void queryClient.invalidateQueries({ queryKey: exploreKeys.overview() });
      void queryClient.invalidateQueries({
        queryKey: exploreKeys.executions(),
      });
      void queryClient.invalidateQueries({
        queryKey: exploreKeys.execution(id),
      });
    },
  });
}
